import React, { useState } from 'react';
import styled from 'styled-components';
import {
  theme,
  mq,
  ShareIcon,
  IdIcon,
  FacebookIcon,
  MailIcon,
  TinyTitleBold
} from '@kvdbil/components';
import ShareEmail from '~/App/shared/components/ShareEmail';
import ShareFacebook from '~/App/shared/components/ShareFacebook';
import AuctionFavouriteButton from '~/App/shared/components/AuctionFavouriteButton';
import { inBusinessTimeZone } from '~/helpers/timezone';
import { useTranslation } from '~/Locale';
import Auction from '~/App/shared/interfaces/Auction';

const Container = styled.div`
  display: flex;
  flex-flow: column;
  justify-content: center;
  align-items: center;
  gap: 2.5rem;

  ${mq('tablet')} {
    flex-flow: row;
  }
`;

const SocialButton = styled.div`
  display: flex;
  align-items: center;
  margin-right: 0.25rem;

  svg {
    font-size: 1.5rem;
  }
`;

const Share = styled(SocialButton)`
  cursor: pointer;

  svg {
    fill: white;
    stroke: ${({ theme }) => theme.colors.grayscaleToned.dark4};
    stroke-width: 2px;
  }

  &:hover {
    svg {
      stroke: ${theme.colors.grayscaleToned.dark1};
    }
  }
`;

const ShareButton = styled(SocialButton)`
  a {
    color: ${({ theme }) => theme.colors.grayscaleToned.dark3};
  }

  &:hover {
    svg {
      fill: ${theme.colors.grayscaleToned.dark1};
    }
  }
`;

const Label = styled(TinyTitleBold)`
  display: inline-block;
  margin-left: 1rem;
  vertical-align: middle;
`;

const MediumIcon = styled.span`
  font-size: 1.5rem;
  vertical-align: -0.5rem;
`;

export interface SocialProps {
  auction: Auction;
  currentLocationPathName: string;
}

const Social = ({ auction, currentLocationPathName }: SocialProps) => {
  const { processObjectId } = auction;
  const [openedShare, setOpenedShare] = useState(false);
  const { t } = useTranslation();
  const preliminaryCloseAt = inBusinessTimeZone(auction.countdownStartAt);

  const handleShareClick = () => {
    setOpenedShare(prev => !prev);
  };

  return (
    <Container>
      {!openedShare && (
        <>
          <AuctionFavouriteButton
            variant="flat"
            color="neutral"
            auctionId={auction.id}
          />

          <Share
            onClick={() => setOpenedShare(!openedShare)}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setOpenedShare(!openedShare);
              }
            }}
            role="button"
            tabIndex={0}
          >
            <ShareIcon />
            <Label>{t('Share')}</Label>
          </Share>

          <SocialButton>
            <IdIcon />
            <Label data-helper="processObjectId">{processObjectId}</Label>
          </SocialButton>
        </>
      )}

      {openedShare && (
        <>
          <ShareButton>
            <ShareFacebook
              url={currentLocationPathName}
              onClick={handleShareClick}
            >
              <MediumIcon>
                <FacebookIcon />
              </MediumIcon>
              <Label>{t('Facebook')}</Label>
            </ShareFacebook>
          </ShareButton>
          <ShareButton>
            <ShareEmail
              onClick={handleShareClick}
              url={currentLocationPathName}
              object={auction.previewTitle ?? ''}
              closeAt={preliminaryCloseAt.format('YYYY-MM-DD HH:mm').toString()}
            >
              <MediumIcon>
                <MailIcon />
              </MediumIcon>
              <Label>{t('Email')}</Label>
            </ShareEmail>
          </ShareButton>
        </>
      )}
    </Container>
  );
};

export default Social;
